import { useState, useEffect, useRef, useCallback } from 'react';
import { useApi, storageApiRef } from '@backstage/core-plugin-api';
import { Option } from '@internal/plugin-org-data-common';
import { useOrgContext } from '../contexts/OrgProvider';

export const useOrgData = () => {
  const storageApi = useApi(storageApiRef).forBucket('user-settings');
  const orgContext = useOrgContext();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selectedDivisions, setSelectedDivisions] = useState<string[]>([]);
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);

  const [unsavedDivisions, setUnsavedDivisions] = useState<string[]>([]);
  const [unsavedPlatforms, setUnsavedPlatforms] = useState<string[]>([]);

  const [currentPlatformOptions, setCurrentPlatformOptions] = useState<Option[]>([]);

  const initializedUnsavedRef = useRef(false);

  useEffect(() => {
    setError(null);
    setLoading(true);

    const divisionSnapshot = storageApi.snapshot<string[]>('division');
    const platformSnapshot = storageApi.snapshot<string[]>('platform');

    const toArray = (snap: ReturnType<typeof storageApi.snapshot<string[]>>) =>
      snap.presence === 'present' && snap.value ? snap.value : [];

    const initialDivisions = toArray(divisionSnapshot);
    const initialPlatforms = toArray(platformSnapshot);

    setSelectedDivisions(initialDivisions);
    setSelectedPlatforms(initialPlatforms);

    if (divisionSnapshot.presence !== 'unknown' && platformSnapshot.presence !== 'unknown') {
      if (!initializedUnsavedRef.current) {
        setUnsavedDivisions(initialDivisions);
        setUnsavedPlatforms(initialPlatforms);
        initializedUnsavedRef.current = true;
      }
      setLoading(false);
    }

    let divisionKnown = divisionSnapshot.presence !== 'unknown';
    let platformKnown = platformSnapshot.presence !== 'unknown';

    const maybeStopLoading = () => {
      if (divisionKnown && platformKnown) setLoading(false);
    };

    const divisionSubscription = storageApi.observe$<string[]>('division').subscribe(snap => {
      const next = snap.presence === 'present' && snap.value ? snap.value : [];
      setSelectedDivisions(next);
      if (!initializedUnsavedRef.current) {
        setUnsavedDivisions(next);
      }
      divisionKnown = true;
      maybeStopLoading();
    });

    const platformSubscription = storageApi.observe$<string[]>('platform').subscribe(snap => {
      const next = snap.presence === 'present' && snap.value ? snap.value : [];
      setSelectedPlatforms(next);
      if (!initializedUnsavedRef.current) {
        setUnsavedPlatforms(next);
      }
      platformKnown = true;
      maybeStopLoading();
    });

    if (!initializedUnsavedRef.current && divisionKnown && platformKnown) {
      initializedUnsavedRef.current = true;
    }

    return () => {
      divisionSubscription.unsubscribe();
      platformSubscription.unsubscribe();
    };
  }, [storageApi]);

  useEffect(() => {
    const platformOptions = orgContext.filterPlatformsForDivisions(unsavedDivisions);
    setCurrentPlatformOptions(platformOptions);

    const validPlatforms = orgContext.validatePlatformsForDivisions(
      unsavedPlatforms,
      unsavedDivisions,
    );
    if (validPlatforms.length !== unsavedPlatforms.length) {
      setUnsavedPlatforms(validPlatforms);
    }
  }, [unsavedDivisions, unsavedPlatforms, orgContext]);

  const hasChanges =
    unsavedDivisions.toString() !== selectedDivisions.toString() ||
    unsavedPlatforms.toString() !== selectedPlatforms.toString();

  const save = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      await storageApi.set('division', unsavedDivisions);
      await storageApi.set('platform', unsavedPlatforms);
      setSelectedDivisions(unsavedDivisions);
      setSelectedPlatforms(unsavedPlatforms);
    } catch {
      setError('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  }, [storageApi, unsavedDivisions, unsavedPlatforms]);

  const reset = useCallback(async () => {
    if (
      unsavedDivisions.length === 0 &&
      unsavedPlatforms.length === 0 &&
      selectedDivisions.length === 0 &&
      selectedPlatforms.length === 0
    ) {
      return;
    }
    setError(null);
    try {
      await storageApi.set('division', []);
      await storageApi.set('platform', []);
      await storageApi.set('userOrgInfo', false);
      setSelectedDivisions([]);
      setSelectedPlatforms([]);
      setUnsavedDivisions([]);
      setUnsavedPlatforms([]);
      initializedUnsavedRef.current = true;
    } catch {
      setError('Failed to reset preferences');
    }
  }, [
    storageApi,
    unsavedDivisions.length,
    unsavedPlatforms.length,
    selectedDivisions.length,
    selectedPlatforms.length,
  ]);

  return {
    loading,
    saving,
    error,
    selectedDivisions,
    selectedPlatforms,
    unsavedDivisions,
    unsavedPlatforms,
    currentPlatformOptions,
    hasChanges,
    setUnsavedDivisions,
    setUnsavedPlatforms,
    save,
    reset,
  };
};
